import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";

const LiveContext = createContext({ subscribe: () => () => {}, send: () => {}, connected: false });

export function LiveProvider({ children }) {
  const listeners = useRef(new Set());
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const url = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws";
    let ws, heartbeat, retryTimer, retries = 0, disposed = false;

    const connect = () => {
      if (disposed) return;
      try { ws = new WebSocket(url); } catch { scheduleRetry(); return; }
      socketRef.current = ws;
      ws.onopen = () => {
        retries = 0;
        setConnected(true);
        heartbeat = setInterval(() => { if (ws.readyState === 1) ws.send("ping"); }, 25000);
      };
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        try {
          const msg = JSON.parse(e.data);
          listeners.current.forEach((fn) => fn(msg));
        } catch (err) {
          console.debug("LiveContext: ignoring non-JSON WS message", err);
        }
      };
      ws.onclose = () => { setConnected(false); clearInterval(heartbeat); scheduleRetry(); };
      ws.onerror = () => ws.close();
    };

    const scheduleRetry = () => {
      if (disposed) return;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(connect, Math.min(30000, 1000 * 2 ** retries++));
    };

    const wake = () => {
      if (document.visibilityState === "visible" && (!ws || ws.readyState > 1)) { retries = 0; clearTimeout(retryTimer); connect(); }
    };

    const startTimer = setTimeout(connect, 0);
    window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", wake);
    return () => {
      disposed = true;
      clearTimeout(startTimer);
      clearInterval(heartbeat);
      clearTimeout(retryTimer);
      window.removeEventListener("online", wake);
      document.removeEventListener("visibilitychange", wake);
      ws?.close();
    };
  }, []);

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  const send = useCallback((obj) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(obj));
  }, []);

  const value = useMemo(() => ({ subscribe, send, connected }), [subscribe, send, connected]);

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export const useLive = () => useContext(LiveContext);
